export const FSBOProcess = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 text-gray-900">
            Our Simple FSBO Purchase Process
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Selling for sale by owner to us is straightforward and stress-free. Here's exactly how it works.
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-teal-200 hidden md:block"></div>
          
          <div className="space-y-12">
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div className="md:text-right">
                <div className="inline-block bg-teal-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-4">
                  1
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900">Initial Contact</h3>
                <p className="text-gray-700 leading-relaxed">
                  Reach out to us by phone, email, or through our website. Tell us about your property: location, number of units, current rents, and your timeline. This conversation takes 10-15 minutes and is completely confidential.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/6007f28954c8891edd7c0d4c7d432840.jpg"
                  alt="Initial contact for apartment building FSBO sale"
                  className="w-full h-64 rounded-lg shadow-lg object-cover"
                />
              </div>
            </div>

            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div className="md:order-2">
                <div className="inline-block bg-teal-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-4">
                  2
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900">Property Information Review</h3>
                <p className="text-gray-700 leading-relaxed">
                  We'll ask you to share basic property information: rent roll, recent expenses, property tax bills, and any major issues you're aware of. You can email these documents or we can review them together on a call. No formal marketing package needed.
                </p>
              </div>
              <div className="relative md:order-1">
                <img
                  src="https://readdy.ai/api/search-image?query=reviewing%20property%20documents%20and%20financial%20statements%2C%20organized%20real%20estate%20paperwork%2C%20professional%20document%20analysis%2C%20clean%20desk%20with%20property%20files%2C%20business%20efficiency%20concept&width=700&height=500&seq=fsbo009&orientation=landscape"
                  alt="Property information review for FSBO apartment building"
                  className="w-full h-64 rounded-lg shadow-lg object-cover"
                />
              </div>
            </div>

            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div className="md:text-right">
                <div className="inline-block bg-teal-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-4">
                  3
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900">Property Walkthrough</h3>
                <p className="text-gray-700 leading-relaxed">
                  We schedule one property visit at a time convenient for you and your tenants. We'll walk the exterior, common areas, and a representative sample of units. This typically takes 1-2 hours. We're respectful of tenant privacy and keep disruption minimal.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20property%20inspection%20walkthrough%20of%20apartment%20building%2C%20investor%20examining%20building%20exterior%20and%20units%2C%20respectful%20property%20tour%2C%20business%20evaluation%20process&width=700&height=500&seq=fsbo010&orientation=landscape"
                  alt="Property walkthrough for FSBO apartment building sale"
                  className="w-full h-64 rounded-lg shadow-lg object-cover"
                />
              </div>
            </div>

            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div className="md:order-2">
                <div className="inline-block bg-teal-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-4">
                  4
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900">Written Offer</h3>
                <p className="text-gray-700 leading-relaxed">
                  Within 48 hours of our walkthrough, we'll present you with a written offer. This includes purchase price, proposed closing date, and any special terms like seller financing or tax structuring options. We'll explain everything clearly and answer all your questions.
                </p>
              </div>
              <div className="relative md:order-1">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20purchase%20offer%20document%20being%20presented%2C%20real%20estate%20contract%20and%20agreement%20papers%2C%20business%20handshake%20over%20signed%20documents%2C%20successful%20deal%20negotiation&width=700&height=500&seq=fsbo011&orientation=landscape"
                  alt="Written offer for FSBO apartment building purchase"
                  className="w-full h-64 rounded-lg shadow-lg object-cover"
                />
              </div>
            </div>

            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div className="md:text-right">
                <div className="inline-block bg-teal-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-4">
                  5
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900">Due Diligence & Closing</h3>
                <p className="text-gray-700 leading-relaxed">
                  Once you accept our offer, we open escrow and conduct our due diligence (typically 10-14 days). We handle all the paperwork and coordinate with the title company. You'll close on your preferred date and receive your proceeds. The entire process from initial contact to closing typically takes 3-4 weeks.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=successful%20real%20estate%20closing%20ceremony%2C%20property%20owner%20receiving%20check%20at%20closing%20table%2C%20signed%20documents%20and%20keys%20exchange%2C%20celebration%20of%20completed%20transaction&width=700&height=500&seq=fsbo012&orientation=landscape"
                  alt="Closing process for FSBO apartment building sale"
                  className="w-full h-64 rounded-lg shadow-lg object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-r from-teal-500 to-blue-500 rounded-xl p-8 md:p-12 text-white text-center">
          <h3 className="text-3xl font-bold mb-4">
            Ready to Sell Your Apartment Building FSBO?
          </h3>
          <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            Skip the agents, save the commissions, and close quickly with a direct buyer who understands your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contact-form"
              className="px-8 py-4 bg-white text-teal-600 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Get Your Offer Today
            </a>
            <a
              href="tel:+11234567890"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap"
            >
              Call Us Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};